/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useEffect } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useTypedSelector } from 'helpers/reducers'
import { useAppDispatch } from 'helpers/store'
import { CompaniesAction } from 'store/companies/CompaniesAction'
import Dates from 'utils/Dates'
import UiButton, { UiButtonSize, UiButtonStyle } from 'components/Ui/UiButton'

const MergeAuditLogPanel: React.FC = () => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const entries = useTypedSelector((state) => state.crmState?.mergeAuditLog ?? [])

    useEffect(() => {
        void dispatch(CompaniesAction.fetchMergeAuditLog())
    }, [dispatch])

    const safeEntries = entries.filter(Boolean)

    if (safeEntries.length === 0) {
        return null
    }

    return (
        <div className={clsx('MergeAuditLogPanel rounded-xl border border-gray-200 p-4')}>
            <div className={clsx('flex items-center justify-between gap-3 mb-2')}>
                <div className={clsx('text-sm font-semibold')}>{t('Recent merge activity')}</div>
                <UiButton
                    size={UiButtonSize.Small}
                    style={UiButtonStyle.Secondary}
                    title={t('Download report') as string}
                    onClick={() => {
                        const blob = new Blob([JSON.stringify(safeEntries, null, 2)], {
                            type: 'application/json;charset=utf-8',
                        })
                        const url = URL.createObjectURL(blob)
                        const link = document.createElement('a')
                        link.href = url
                        link.download = 'CRM_Merge_Report.json'
                        link.click()
                        URL.revokeObjectURL(url)
                    }}
                />
            </div>
            <div className="mb-3 text-xs text-gray-600 dark:text-gray-300">
                {t('Merge history is stored on the server for audit and rollback support.')}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full min-w-[900px] text-left text-xs">
                    <thead>
                        <tr className="border-b border-gray-200 text-gray-500 dark:border-gray-700">
                            <th className="px-2 py-2 font-semibold">{t('When')}</th>
                            <th className="px-2 py-2 font-semibold">{t('Main company')}</th>
                            <th className="px-2 py-2 font-semibold">{t('Merged company')}</th>
                            <th className="px-2 py-2 font-semibold">related_model_id</th>
                            <th className="px-2 py-2 font-semibold">{t('Action')}</th>
                            <th className="px-2 py-2 font-semibold">{t('Attribute')}</th>
                            <th className="px-2 py-2 font-semibold">{t('Replaced')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {safeEntries.map((entry) => (
                            <tr key={entry.id} className="border-b border-gray-100 align-top dark:border-gray-800">
                                <td className="whitespace-nowrap px-2 py-2 text-gray-500">
                                    {Dates.dateTime(entry.createdAt)}
                                </td>
                                <td className="px-2 py-2 font-medium">
                                    {entry.mainCompanyName || '—'}
                                    <div className="text-[10px] font-normal text-gray-500">
                                        main_model_id: {entry.mainModelId || entry.mainCompanyId || '—'}
                                    </div>
                                </td>
                                <td className="px-2 py-2">
                                    {entry.mergedCompanyNames?.[0] || '—'}
                                    <div className="text-[10px] text-gray-500">
                                        merged_model_id: {entry.mergedModelId || entry.mergedCompanyIds?.join(', ') || '—'}
                                    </div>
                                </td>
                                <td className="px-2 py-2">
                                    {entry.relatedModelId || '—'}
                                    {entry.relatedModelType && (
                                        <div className="mt-0.5 text-[10px] text-gray-500">{entry.relatedModelType}</div>
                                    )}
                                </td>
                                <td className="px-2 py-2">{entry.action || '—'}</td>
                                <td className="px-2 py-2">{entry.attribute || '—'}</td>
                                <td className="max-w-[320px] px-2 py-2">
                                    <span className="break-words text-red-700 dark:text-red-300">
                                        {entry.oldValue ?? '∅'}
                                    </span>
                                    <span className="px-1 text-gray-400">→</span>
                                    <span className="break-words text-green-700 dark:text-green-300">
                                        {entry.newValue ?? '∅'}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default MergeAuditLogPanel
